// ShopSection.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { RichCoinIcon } from '../assets/icons/Icons';
import { Icon } from '@iconify/react';
import { useTonDeposit } from '../hooks/useTonDeposit';

// ----------------------------
// CONFIGURATION
// ----------------------------

const SHOP_ITEMS = [
  { 
    id: 1,
    amount: 10000, 
    ton: 1, 
    testnetTon: 0.1, // All items cost 0.1 TON in testnet
    discount: 10,
    discountColor: '#FF0034'
  },
  { 
    id: 2,
    amount: 55000, 
    ton: 5, 
    testnetTon: 0.1,
    discount: 0,
    discountColor: null
  },
  { 
    id: 3,
    amount: 130000, 
    ton: 10, 
    testnetTon: 0.1,
    discount: 25,
    discountColor: '#FF0034'
  },
  { 
    id: 4,
    amount: 390000, 
    ton: 25, 
    testnetTon: 0.1,
    discount: 30,
    discountColor: '#FF0034'
  }
];

const ShopContainer = styled.div`
  margin: 16px;
  margin-top: 20px;
`;

const ShopHeader = styled.div`
  margin-bottom: 12px;
  text-align: left;
`;

const ShopTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 4px;
  color: #000000;
`;

const ShopDescription = styled.p`
  font-size: 14px;
  color: #757575;
  margin: 0;
  font-weight: 400;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
`;

const ShopItem = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 20px 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: transform 0.2s ease;
  
  &:active {
    transform: scale(0.98);
  }
`;

const CoinDisplay = styled.div`
  margin: 8px 0 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const CoinAmount = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #000000;
  text-align: center;
`;

const BuyButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 600;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  cursor: pointer;
  transition: transform 0.2s ease;
  opacity: ${props => props.$loading ? 0.7 : 1};
  
  &:active {
    transform: scale(0.96);
  }
  
  &:disabled {
    cursor: not-allowed;
    transform: none;
  }
  
  img {
    height: 20px;
    width: 20px;
  }
`;

const TestnetBadge = styled.div`
  background-color: #4CAF50;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #4CAF50;
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  font-weight: 500;
  z-index: 1000;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  animation: fadeInOut 3s ease-in-out;

  @keyframes fadeInOut {
    0%, 100% { opacity: 0; }
    20%, 80% { opacity: 1; }
  }
`;

const ErrorMessage = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #f44336;
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  font-weight: 500;
  z-index: 1000;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  animation: fadeInOut 3s ease-in-out;

  @keyframes fadeInOut {
    0%, 100% { opacity: 0; }
    20%, 80% { opacity: 1; }
  }
`;

const DiscountTag = styled.div`
  position: absolute;
  top: -8px;
  left: -8px;
  background-color: ${props => props.$color || '#FF0034'};
  color: white;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  z-index: 1;
`;

const ShopSection = () => {
  const { sendDepositTransaction } = useTonDeposit();
  const [loadingItems, setLoadingItems] = useState(new Set());
  const [message, setMessage] = useState(null);

  const handlePurchase = async (item) => {
    // Add item to loading state
    setLoadingItems(prev => new Set(prev).add(item.id));

    try {
      const result = await sendDepositTransaction(item.ton);
      
      if (result?.success) {
        setMessage({
          type: 'success',
          text: `Purchase successful! You bought ${item.amount.toLocaleString()} coins for ${item.testnetTon} TON (testnet)`
        });
        
        // Here you could add logic to:
        // 1. Update user's coin balance in the database
        // 2. Refresh user data
        // 3. Show transaction confirmation
      } else {
        setMessage({
          type: 'error',
          text: result?.error || 'Transaction failed'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to process purchase'
      });
    } finally {
      // Remove item from loading state
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    }
  };

  return (
    <ShopContainer>
      <ShopHeader>
        <ShopTitle>Shop</ShopTitle>
        <ShopDescription>Limited availability—get yours before they vanish! (Testnet Mode)</ShopDescription>
      </ShopHeader>
      
      <ItemsGrid>
        {SHOP_ITEMS.map((item) => (
          <ShopItem key={item.id}>
            {item.discount > 0 && (
              <DiscountTag $color={item.discountColor}>
                {item.discount}% OFF
              </DiscountTag>
            )}
            <CoinDisplay>
              <RichCoinIcon width="64" height="64" />
              <CoinAmount>{item.amount.toLocaleString()}</CoinAmount>
            </CoinDisplay>
            <BuyButton 
              onClick={() => handlePurchase(item)}
              disabled={loadingItems.has(item.id)}
              $loading={loadingItems.has(item.id)}
            >
              {loadingItems.has(item.id) ? (
                <>
                  <Icon icon="eos-icons:loading" width="20" height="20" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon icon="simple-icons:ton" width="20" height="20" color="#0088CC" />
                  {item.testnetTon} TON
                </>
              )}
            </BuyButton>
            <TestnetBadge>TESTNET</TestnetBadge>
          </ShopItem>
        ))}
      </ItemsGrid>

      {/* Success/Error Messages */}
      {message?.type === 'success' && (
        <SuccessMessage>
          {message.text}
        </SuccessMessage>
      )}
      
      {message?.type === 'error' && (
        <ErrorMessage>
          {message.text}
        </ErrorMessage>
      )}
    </ShopContainer>
  );
};

export default ShopSection;
